import { Chip, Box, Typography, Link } from "@mui/material";
import { Icon } from "@iconify/react";
import { Link as RouterLink } from "react-router-dom";
import { ChargerActionMenu } from "./ChargerActionMenu";
import { yearFirstDateTimeFormat, fChargerLogDateTime } from "src/utils/formatTime";
import Tooltip from "@mui/material/Tooltip";

const getConnectorStatusColor = (status = "") => {
  const s = status.toLowerCase();
  if (s === "available") return "success";
  if (s === "charging") return "primary";
  if (s === "preparing" || s === "finishing") return "info";
  if (s === "faulted") return "error";
  if (s.includes("suspended") || s === "unavailable") return "warning";
  if (s === "reserved") return "secondary";
  return "default";
};

const textCell = (value) =>
  value ? (
    <Tooltip title={value} placement="top">
      <Typography
        variant="body2"
        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}
      >
        {value}
      </Typography>
    </Tooltip>
  ) : (
    <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
      —
    </Typography>
  );

export const statusHistoryColumns = [
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    minWidth: 150,
    renderCell: ({ value }) => (
      <Chip
        label={value || "—"}
        size="small"
        color={getConnectorStatusColor(value)}
        variant="filled"
        sx={{ fontWeight: 600, fontSize: "0.75rem" }}
      />
    ),
  },
  {
    field: "timestamp",
    headerName: "Timestamp",
    flex: 1.3,
    minWidth: 190,
    renderCell: ({ value }) =>
      value ? (
        <Tooltip title={yearFirstDateTimeFormat(value)} placement="top">
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {fChargerLogDateTime(value)}
          </Typography>
        </Tooltip>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
          —
        </Typography>
      ),
  },
  {
    field: "error_code",
    headerName: "Error Code",
    flex: 1,
    minWidth: 150,
    renderCell: ({ value }) =>
      value && value !== "NoError" ? (
        <Chip
          label={value}
          size="small"
          color="error"
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: "0.75rem" }}
        />
      ) : (
        <Chip
          label="No Error"
          size="small"
          color="success"
          variant="outlined"
          sx={{ fontWeight: 500, fontSize: "0.75rem" }}
        />
      ),
  },
  {
    field: "error_info",
    headerName: "Error Info",
    flex: 1,
    minWidth: 140,
    renderCell: ({ value }) => textCell(value),
  },
  {
    field: "vendor_id",
    headerName: "Vendor ID",
    flex: 1,
    minWidth: 140,
    renderCell: ({ value }) => textCell(value),
  },
  {
    field: "vendor_error_code",
    headerName: "Vendor Error Code",
    flex: 1,
    minWidth: 160,
    renderCell: ({ value }) => textCell(value),
  },
];

export const chargerPointColumns = (isReadOnly) => [
  {
    field: "Actions",
    width: 50,
    sortable: false,
    headerName: "",
    renderCell: ({ id, row }) => {
      return <ChargerActionMenu id={id} row={row} />;
    },
  },
  {
    field: "chargepoint_id",
    headerName: "Chargebox ID",
    flex: 1.2,
    minWidth: 180,
    renderCell: ({ value, id }) => (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{
          p: 0.5,
          borderRadius: 1,
          background: (theme) => theme.palette.primary.lighter,
          mr: 1,
          display: 'flex',
          alignItems: 'center'
        }}>
          <Icon icon="eva:charging-outline" width={16} height={16} style={{ 
            color: (theme) => theme.palette.primary.main 
          }} />
        </Box>
        {isReadOnly ? (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value}
          </Typography>
        ) : (
          <Link 
            underline="none" 
            component={RouterLink} 
            to={`${id}`}
            sx={{ 
              fontWeight: 500,
              color: (theme) => theme.palette.primary.main,
              '&:hover': {
                color: (theme) => theme.palette.primary.dark,
                textDecoration: 'underline'
              }
            }}
          >
            {value}
          </Link>
        )}
      </Box>
    ),
  },
  {
    field: "team_name",
    headerName: "Team",
    flex: 1,
    minWidth: 120,
    renderCell: ({ value }) => (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {value ? (
          <Chip
            label={value}
            size="small"
            variant="outlined"
            sx={{
              backgroundColor: (theme) => theme.palette.background.paper,
              borderColor: (theme) => theme.palette.divider,
              color: (theme) => theme.palette.text.primary,
              fontWeight: 500,
              fontSize: '0.75rem'
            }}
          />
        ) : (
          <Typography variant="body2" sx={{ 
            color: 'text.secondary', 
            fontStyle: 'italic' 
          }}>
            No Team
          </Typography>
        )}
      </Box>
    ),
  },
  {
    field: "street",
    headerName: "Address",
    flex: 1.5,
    minWidth: 200,
    renderCell: ({ value }) => (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
        <Box sx={{
          p: 0.5,
          borderRadius: 1,
          background: (theme) => theme.palette.info.lighter,
          mr: 1,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          mt: 0.2
        }}>
          <Icon icon="eva:pin-outline" width={14} height={14} style={{ 
            color: (theme) => theme.palette.info.main 
          }} />
        </Box>
        <Tooltip title={value} placement="top">
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 500,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              lineHeight: 1.4,
              maxWidth: '100%'
            }}
          >
            {value}
          </Typography>
        </Tooltip>
      </Box>
    ),
  },
  {
    field: "description",
    headerName: "Description",
    flex: 1,
    minWidth: 150,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <Typography variant="body2" sx={{ 
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%'
        }}>
          {value}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: "chargingSiteName",
    headerName: "Charging Site",
    flex: 1,
    minWidth: 140,
    renderCell: ({ value }) => (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {value ? (
          <Chip
            label={value}
            size="small"
            variant="filled"
            color="info"
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
            }}
          />
        ) : (
          <Typography variant="body2" sx={{ 
            color: 'text.secondary', 
            fontStyle: 'italic' 
          }}>
            No Site
          </Typography>
        )}
      </Box>
    ),
  },
  {
    field: "visibility",
    headerName: "Visibility",
    flex: 0.8,
    minWidth: 100,
    renderCell: ({ value }) => {
      const isPublic = Number(value) === 1;
      return (
        <Chip
          icon={<Icon icon={isPublic ? "eva:globe-outline" : "eva:lock-outline"} width={16} height={16} />}
          label={isPublic ? "Public" : "Private"}
          size="small"
          color={isPublic ? "info" : "default"}
          variant="filled"
          sx={{ fontWeight: 500, fontSize: '0.75rem' }}
        />
      );
    },
  },
  {
    field: "status",
    headerName: "Status",
    flex: 0.8,
    minWidth: 100,
    renderCell: ({ value }) => {
      const isActive = value === "Active";
      return (
        <Chip
          icon={<Icon icon={isActive ? "eva:checkmark-circle-outline" : "eva:close-circle-outline"} width={16} height={16} />}
          label={value}
          size="small"
          color={isActive ? "success" : "error"}
          variant="filled"
          sx={{ fontWeight: 500, fontSize: '0.75rem' }}
        />
      );
    },
  },
  {
    field: "lastHeartbeatTimestamp",
    headerName: "Last Connected",
    flex: 1,
    minWidth: 160,
    renderCell: ({ value }) => {
      const isRecent = new Date(value) > new Date(Date.now() - 24 * 60 * 60 * 1000); // Within 24 hours
      const isOld = new Date(value) < new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // Older than 7 days
      
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
            p: 0.5,
            borderRadius: 1,
            background: isRecent 
              ? (theme) => theme.palette.success.lighter
              : isOld 
                ? (theme) => theme.palette.error.lighter
                : (theme) => theme.palette.warning.lighter,
            mr: 1,
            display: 'flex',
            alignItems: 'center'
          }}>
            <Icon 
              icon={isRecent ? "eva:checkmark-outline" : isOld ? "eva:close-outline" : "eva:clock-outline"} 
              width={14} 
              height={14} 
              style={{ 
                color: isRecent 
                  ? (theme) => theme.palette.success.main
                  : isOld 
                    ? (theme) => theme.palette.error.main
                    : (theme) => theme.palette.warning.main
              }} 
            />
          </Box>
          <Tooltip title={yearFirstDateTimeFormat(value)} placement="top">
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500,
                color: isRecent 
                  ? (theme) => theme.palette.success.main
                  : isOld 
                    ? (theme) => theme.palette.error.main
                    : (theme) => theme.palette.text.primary
              }}
            >
              {yearFirstDateTimeFormat(value)}
            </Typography>
          </Tooltip>
        </Box>
      );
    },
  },
];

export const ChargerFormTabs = [
  { value: "general", label: "GENERAL" },
  { value: "address", label: "ADDRESS" },
  { value: "settings", label: "SETTINGS" },
  { value: "remote operations", label: "REMOTE OPERATIONS" },
  { value: "operating_instruction", label: "OPERATING INSTRUCTIONS" },
  { value: "report_incidents", label: "REPORT INCIDENTS" },
  { value: "status_history", label: "STATUS HISTORY" },
  // {value:"config",label:"CONFIG"},
  { value: "logs", label: "LOGS" },
];

// The Payout page and its sidebar entry are Amik-branded instances only.
export const isAmikPayoutDomain = () => {
  try {
    const host = window.location.hostname || "";
    return (
      /(^|\.)amiktech\.in$/i.test(host) ||
      /(^|\.)amiktechnologies\.com$/i.test(host)
    );
  } catch (e) {
    return false;
  }
};
export const hideForTabs = ["remote operations", "logs", "config", "report_incidents", "status_history"];
export const getChargerDefaultValues = ({ teamId }) => ({
  chargepoint_id: "",
  chargepoint_name: "",
  excludeFromEmail: false,
          chargingSiteId: null,
  registrationStatus: "Pending",
  charger_type: "DC",
  description: "",
  adminAddress: "",
  locationLongitude: "",
  locationLatitude: "",
  note: "",
  endpointAddress: "",
  ocppProtocol: "",
  chargePointVendor: "",
  chargePointSerialNumber: "",
  chargeBoxSerialNumber: "",
  fwVersion: "",
  fwUpdateStatus: "",
  fwUpdateTimestamp: "",
  iccid: "",
  imsi: "",
  meterType: "",
  meterSerialNumber: "",
  diagnosticsStatus: "",
  diagnosticsTimestamp: "",
  lastHeartbeatTimestamp: "",
  name: "",
  street: "",
  houseNumber: "",
  zipCode: "",
  city: "",
  country: "",
  state: "",
  priceGroupId: null,
  companyId: teamId,
  connectors: [
    { connectorId: "", connectorName: "", kw: "", volt: "", connectorType: "", priceGroupId: "" },
  ],
  showOnPortal: false,
  preparingState: false,
  employeeOrResident: true,
  payWithWallet: true,
  payWithCard: true,
  enableNotifications: true,
  notificationRecipients: "",
  OcppStationBooted: true,
  OcppStationStatusFailure: true,
  OcppStationWebSocketConnected: true,
  OcppStationWebSocketDisconnected: true,
  OcppTransactionStarted: true,
  OcppTransactionEnded: true,
  tolerance_watt: 300,
  meter_interval_sec: 60,
  tolerance_updated_at: null,
  amenities: [],
  photos: [],
  operator_email: "",
  operator_number1: "",
  operator_number2: "",
  faq: [{ question: "", answer: "", id: 0 }],
  charger_category: "paid",
  access_level: "Public",
  isActive: false,
});

export const NOTIFICATION_CONSTANTS = [
  "OcppStationBooted",
  "OcppStationStatusFailure",
  "OcppStationWebSocketConnected",
  "OcppStationWebSocketDisconnected",
  "OcppTransactionStarted",
  "OcppTransactionEnded",
];

export const AMENITIES = [
  { text: "Gym", value: "GYM" },
  { text: "Cinema", value: "THEATER" },
  { text: "Salon", value: "SALON" },
  { text: "Pilgrimage Sites", value: "PILGRIMAGE SITE" },
  { text: "Public Washroom", value: "PUBLIC WASHROOM" },
  { text: "Park", value: "PARK" },
  { text: "Kid Acitivity Zone", value: "KID ACTIVITY ZONE" },
  { text: "Auto Service Center", value: "AUTO SERVICE" },
  { text: "Medical Store", value: "MEDICAL STORE" },
  { text: "Hospital", value: "HOSPITAL" },
  { text: "24/7 Monitoring", value: "24/7 MONITORING" },
  { text: "Hotel", value: "HOTEL" },
  { text: "ATM", value: "ATM" },
  { text: "Restaurant", value: "RESTAURANT" },
  { text: "Shopping Center", value: "SHOPPING" },
  { text: "Parking", value: "PARKING" },
];

export const reportIssuesColumn = [
  {
    field: "serial_number",
    headerName: "Serial",
    width: 120,
  },
  {
    field: "charge_box_id",
    headerName: "Charger ID",
    width: 150,
  },
  {
    field: "created_at",
    headerName: "Date & Time",
    type: "date",
    width: 180,
    renderCell: ({ value }) => (
      <Tooltip title={yearFirstDateTimeFormat(value)} placement="top">
        <span>{yearFirstDateTimeFormat(value)}</span>
      </Tooltip>
    ),
  },
  {
    field: "category",
    headerName: "Reported Issues Category",
    flex: 1,
    minWidth: 200,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span>{value}</span>
      </Tooltip>
    ),
  },
  {
    field: "comment",
    headerName: "Comments",
    flex: 1,
    minWidth: 200,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span>{value}</span>
      </Tooltip>
    ),
  },
];
